<script setup lang="ts">
import { useProvetHead } from '@/composables/useProvetHead.js'
import useTranslation from '@/composables/useTranslation.js'
import { dashboardUrl } from '@/utils/url.js'
import { isValidRouteParamNumericId } from '@/utils/navigation.js'
import { LayoutQueryParam } from '@/utils/querystring.js'
import BaseIconArrowLogical from '@/components/base/IconArrowLogical.vue'
import BaseRouterLinkButton from '@/components/base/router-link/Button.vue'
const props = defineProps<{
  error:
    | Error
    | { statusCode: number; message: string; data?: { path?: string } }
}>()

const config = useRuntimeConfig()
const route = useRoute()
const tPage = useTranslation('pages.error')
useProvetHead()

const provetId = computed(() => {
  if (!isValidRouteParamNumericId(route, 'provetId')) {
    return undefined
  }

  return route.params.provetId
})

const status = computed(() =>
  'statusCode' in props.error ? props.error.statusCode : undefined,
)

const pathname = computed(() =>
  'data' in props.error ? props.error.data?.path : undefined,
)

const canGoBack = computed(() => {
  return window.history.length > 1
})

const refreshPage = () => {
  window.location.reload()
}
</script>

<template>
  <nord-layout>
    <div
      :class="[
        'n:mx-auto',
        'n:mbs-xl',
        config.public.isDev ? 'n:container-l' : 'n:container-xs',
      ]"
    >
      <nord-card padding="l">
        <template v-if="status === 404">
          <h1 slot="header">{{ tPage('pageNotFound.header') }}</h1>
          <p v-if="pathname">
            <i18next :translation="tPage('pageNotFound.description')">
              <template #url>
                <code>{{ pathname }}</code>
              </template>
            </i18next>
          </p>
        </template>

        <template v-else>
          <h1 slot="header">
            {{ tPage('unexpectedError.header') }}
          </h1>
          <p>{{ tPage('unexpectedError.description') }}</p>
        </template>

        <nord-stack v-if="config.public.isDev" class="n:mbs-m">
          <p>
            <code>
              {{
                'name' in error
                  ? `${error.name}: "${error.message}"`
                  : error.message
              }}
            </code>
          </p>
          <pre class="overflow-x">{{ JSON.stringify(error, null, 2) }}</pre>
          <details v-if="'stack' in error">
            <summary class="n:mbe-s">{{ 'Stack trace' }}</summary>
            <pre class="overflow-x n:bg-default n:p-m">{{ error.stack }}</pre>
          </details>
        </nord-stack>

        <nord-button-group slot="footer" variant="spaced">
          <nord-button
            v-if="route.meta.layout === LayoutQueryParam.Iframe"
            variant="primary"
            @click="refreshPage"
          >
            <nord-icon slot="start" name="arrow-refresh" />
            {{ tPage('buttonReload') }}
          </nord-button>
          <template v-else>
            <nord-button
              v-if="canGoBack"
              variant="primary"
              @click="$router.back()"
            >
              <BaseIconArrowLogical slot="start" name="arrow-start-small" />
              {{ tPage('buttonGoBack') }}
            </nord-button>

            <BaseRouterLinkButton
              v-if="provetId"
              :to="dashboardUrl(provetId).toString()"
            >
              {{ tPage('buttonReturnHome') }}
            </BaseRouterLinkButton>
          </template>
        </nord-button-group>
      </nord-card>
    </div>
  </nord-layout>
</template>

<style scoped>
.overflow-x {
  overflow-x: auto;
}
</style>
